import * as React from 'react';
import { useMemo } from 'react';

import { X } from '@phosphor-icons/react';
import { motion, useReducedMotion } from 'motion/react';

import { useClickOutside } from './hooks/useClickOutside';

import { cn } from '../../utils/cn';

import type { SimpleModalProps } from './types';

// ---------------------------------------------------------------------------
// Animation constants
// ---------------------------------------------------------------------------

const TRANSITION = {
  spring: { type: 'spring' as const, duration: 0.4, bounce: 0.2 },
  enter: { duration: 0.4, ease: 'easeOut' as const },
  exit: { duration: 0.4, ease: 'easeIn' as const },
  reducedMotion: { duration: 0.2, ease: 'easeOut' as const },
};

// ---------------------------------------------------------------------------
// Placement helpers
// ---------------------------------------------------------------------------

const DESKTOP_ALIGNMENT_MAP = {
  bottom: 'md:items-end',
  top: 'md:items-start',
  center: 'md:items-center',
} as const;

const MOBILE_ALIGNMENT_MAP = {
  bottom: 'items-end',
  top: 'items-start',
  center: 'items-center',
} as const;

const getOverlayAlignmentClasses = (
  placement: SimpleModalProps['placement'],
  mobilePlacement: SimpleModalProps['placement'],
) => {
  const mobileAlign = MOBILE_ALIGNMENT_MAP[mobilePlacement ?? 'center'];
  const desktopAlign = DESKTOP_ALIGNMENT_MAP[placement ?? 'center'];
  return cn('flex justify-center', mobileAlign, desktopAlign);
};

const getContentClassNames = (
  placement: SimpleModalProps['placement'],
  mobilePlacement: SimpleModalProps['placement'],
) => {
  const isEdgeAnchored = (p?: string) => p === 'bottom' || p === 'top';
  const mobileWidth = isEdgeAnchored(mobilePlacement) ? 'w-full' : 'w-[95%]';
  const desktopWidth = isEdgeAnchored(placement) ? 'md:w-full' : 'md:w-fit';
  return cn('h-fit', mobileWidth, desktopWidth);
};

// ---------------------------------------------------------------------------
// Backdrop blur helpers
// ---------------------------------------------------------------------------

const getBlurClass = (backdropBlur: SimpleModalProps['backdropBlur']) => {
  if (backdropBlur === 'subtle') return 'backdrop-blur-sm';
  if (backdropBlur) return 'backdrop-blur-lg';
  return '';
};

const getBlurSize = (backdropBlur: SimpleModalProps['backdropBlur']) => {
  if (backdropBlur === 'subtle') return '4px';
  if (backdropBlur) return '16px';
  return undefined;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SimpleModal: React.FC<SimpleModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  overlayClassName,
  style,
  placement,
  mobilePlacement = 'bottom',
  showRoundedBorders = true,
  showCloseButton = true,
  shouldCloseOnOutsideClick = true,
  shouldDisableScroll = true,
  hideOverflow = true,
  backdropBlur = false,
  springAnimation = true,
}) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const [isPresent, setIsPresent] = React.useState(isOpen);
  const [isClosing, setIsClosing] = React.useState(false);

  // Sync presence state with isOpen prop
  React.useEffect(() => {
    if (isOpen) {
      setIsPresent(true);
      setIsClosing(false);
    } else {
      // Parent set isOpen=false externally — trigger exit animation
      setIsPresent((prev) => {
        if (prev) setIsClosing(true);
        return prev;
      });
    }
  }, [isOpen]);

  // Two-phase close: trigger exit animation first, then call onClose after it completes
  const handleClose = React.useCallback(
    (value: boolean) => {
      if (!value) {
        setIsClosing((prev) => {
          if (prev) return prev; // already closing, ignore
          return true;
        });
      } else {
        onClose?.(value);
      }
    },
    [onClose],
  );

  const handleAnimationComplete = React.useCallback(() => {
    if (isClosing) {
      setIsPresent(false);
      setIsClosing(false);
      onClose?.(false);
    }
  }, [isClosing, onClose]);

  useClickOutside({
    isOpen: isPresent,
    onClose: handleClose,
    contentRef,
    shouldCloseOnOutsideClick,
    shouldDisableScroll,
  });

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const overlayAlignmentClasses = useMemo(
    () => getOverlayAlignmentClasses(placement, mobilePlacement),
    [placement, mobilePlacement],
  );

  const contentClassNames = useMemo(
    () => getContentClassNames(placement, mobilePlacement),
    [placement, mobilePlacement],
  );

  const isAnimating = springAnimation && !prefersReducedMotion;
  const isAnimatedOpen = isOpen && !isClosing;
  const slideDirection = placement === 'top' ? '-100%' : '100%';

  const contentTransition = isAnimating ? TRANSITION.spring : TRANSITION.reducedMotion;
  const overlayTransition = isAnimatedOpen ? TRANSITION.enter : TRANSITION.exit;

  const blurClass = getBlurClass(backdropBlur);
  const blurSize = getBlurSize(backdropBlur);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!isPresent) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isAnimatedOpen ? 1 : 0 }}
      transition={overlayTransition}
      onAnimationComplete={handleAnimationComplete}
      className={cn(
        'fixed inset-0 z-50',
        hideOverflow && 'overflow-hidden',
        overlayAlignmentClasses,
        'bg-black/80',
        'bg-[--overlay-background-color]',
        blurClass,
        overlayClassName,
      )}
      style={{
        ...style,
        ...(blurSize ? { WebkitBackdropFilter: `blur(${blurSize})` } : {}),
      }}
    >
      <motion.div
        ref={contentRef}
        initial={{ y: slideDirection }}
        animate={{ y: isAnimatedOpen ? 0 : slideDirection }}
        transition={isAnimatedOpen ? contentTransition : TRANSITION.exit}
        className={cn('relative', contentClassNames, showRoundedBorders && 'rounded-lg', 'bg-background', className)}
      >
        {showCloseButton && (
          <button
            type="button"
            onClick={() => handleClose(false)}
            className={cn(
              'z-10 absolute right-[--modal-close-button-placement-right] top-[--modal-close-button-placement-top] rounded-sm opacity-70',
              'hover:opacity-100 transition-opacity',
              'focus:outline-none focus:ring-2 focus:ring-offset-2',
              'text-[--modal-close-button-color]',
            )}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {children}
      </motion.div>
    </motion.div>
  );
};

export default SimpleModal;
